import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "src/components/ui/button";
import {
  ApiPaymentMethods,
  CONSTANTS_STRINGS,
  RoutesPath,
} from "src/constants";
import { directDebitSuccessRoutes } from "src/constants/navigation";
import useCreateDirectDebit from "src/hooks/api/useCreateDirectDebit";
import useDirectDebitPay from "src/hooks/api/useDirectDebitPay";
import { useCheckoutDataContext } from "src/providers/SharedDataProvider";
import { DirectDebtPaymentMethodChangeFormData } from "src/schemas/DirectDebtFormSchema";
import { CheckoutHelper } from "src/utils";
import { debugLog } from "src/utils/debugLog";
import { getDeviceData } from "src/utils/DeviceHelper";

interface AuthorizePaymentButtonProps {
  className?: string;
  accountData: DirectDebtPaymentMethodChangeFormData | undefined;
}

const AuthorizePaymentButton: React.FC<AuthorizePaymentButtonProps> = ({
  className = "text-base hover:cursor-pointer flex-1 md:flex-none",
  accountData,
}) => {
  const queryClient = useQueryClient();
  const { token } = useParams();
  const navigate = useNavigate();
  const checkoutData = useCheckoutDataContext();
  const { checkoutPreferences } = checkoutData;
  const isPaymentMethodChange =
    !CheckoutHelper.isInstallmentRequired(checkoutData);
  const { mutateAsync: createDirectDebit, isPending } = useCreateDirectDebit();
  const { mutateAsync: directDebitPay, isPending: isDirectDebitPayPending } =
    useDirectDebitPay();

  const handleClick = async () => {
    if (!accountData) return;
    try {
      const createdDirectDebitResponse = await createDirectDebit(
        {
          token,
          data: {
            account: accountData.account + accountData.digit,
            agency: accountData.agency,
            bankNumber: accountData.bankData.number,
          },
        },
        {
          onError: (error) => {
            toast.error(error.cause);
          },
        },
      );

      const device = await getDeviceData();

      const checkoutPreferencesPayload: RequestCheckoutPreferences = {};

      if (!isPaymentMethodChange) {
        const installmentOption = accountData.installmentOption as
          { parcel: number; id: string } | undefined;
        const isSingleInstallment = installmentOption?.parcel === 1;

        if (accountData.billingDay && !isSingleInstallment) {
          checkoutPreferencesPayload.billingDate = accountData.billingDay;
        }
        checkoutPreferencesPayload.installmentId = installmentOption?.id;
      }

      await directDebitPay(
        {
          token,
          data: {
            device,
            paymentInstruments: [
              {
                id: createdDirectDebitResponse.id,
                type: ApiPaymentMethods.DIRECT_DEBIT,
                value: checkoutPreferences.totalAmount,
              },
            ],
            checkoutPreferences: checkoutPreferencesPayload,
          },
        },
        {
          onSuccess: (data) => {
            const responseStatus = data.status;
            queryClient.setQueryData(
              ["checkout", token],
              (oldData: CheckoutType) => ({
                ...oldData,
                ...data,
              }),
            );
            if (
              !Object.prototype.hasOwnProperty.call(
                directDebitSuccessRoutes,
                responseStatus,
              )
            ) {
              void navigate(`/${RoutesPath.HOME}`);
              return;
            }

            void navigate(
              `/${token}${directDebitSuccessRoutes[responseStatus]}`,
            );
          },
          onError: (error) => {
            toast.error(error.cause);
          },
        },
      );
    } catch (error) {
      debugLog({ "DEBUG - Payment Error:": error });
    }
  };

  if (!accountData) return null;

  return (
    <Button
      variant={"primary"}
      size="primary"
      className={className}
      type="button"
      onClick={() => void handleClick()}
      disabled={isPending || isDirectDebitPayPending}
    >
      {(isPending || isDirectDebitPayPending) && (
        <Loader2 className="mr-2 size-4 animate-spin" />
      )}
      <span className="md:hidden">{CONSTANTS_STRINGS.CONTINUE}</span>
      <span className="hidden md:block">
        {CONSTANTS_STRINGS.DIRECT_DEBIT_AUTHORIZATION_BUTTON_TEXT}
      </span>
    </Button>
  );
};

export default AuthorizePaymentButton;
